import { computed, Injectable, Signal, signal } from "@angular/core";

import { ToasterSeverities } from "@enums/toaster-severities";

/** One notification, as a toast and as a line in the history. */
export interface AppNotification {
  readonly id: number;
  readonly severity: ToasterSeverities;
  /** Translation key of the short heading ("Saved", "Not saved"). */
  readonly title?: string;
  /** Translation key of the description under it. */
  readonly message: string;
  /** Interpolation values for the title and description, e.g. a name. */
  readonly params?: Readonly<Record<string, unknown>>;
  /** Where it was raised: the page, then the dialog, already in words. */
  readonly where?: ReadonlyArray<string>;
  /** Already-resolved detail text, e.g. the backend's error message. */
  readonly detail?: string;
  /** When it was first raised. */
  readonly at: Date;
  /** How many times the same notification was raised while it was showing. */
  readonly count: number;
}

/** What NotificationService adds to a notification besides its message. */
export interface NotificationExtras {
  readonly title?: string;
  readonly params?: Readonly<Record<string, unknown>>;
  readonly where?: ReadonlyArray<string>;
}

/** How long a non-error toast stays up. Errors stay until dismissed. */
export const TOAST_DURATION_MS = 5000;

/** More than this many toasts at once is noise; the oldest make room. */
export const MAX_VISIBLE_TOASTS = 4;

/** The history keeps this session's last notifications, newest first. */
export const HISTORY_LIMIT = 50;

interface Timer {
  handle: ReturnType<typeof setTimeout> | undefined;
  remaining: number;
  startedAt: number;
}

/**
 * The notification system's state.
 *
 * Replaces MatSnackBar, which shows one message at a time: a second toast
 * replaced the first before it could be read, and once gone a message was
 * gone for good. Here toasts stack, pause while hovered, errors stay until
 * dismissed, and everything raised this session is kept in a history the
 * header's bell opens.
 */
@Injectable({ providedIn: "root" })
export class NotificationStore {
  private readonly toastList = signal<ReadonlyArray<AppNotification>>([]);
  private readonly historyList = signal<ReadonlyArray<AppNotification>>([]);
  private readonly unreadCount = signal(0);
  private readonly timers = new Map<number, Timer>();
  private nextId = 1;

  readonly toasts: Signal<ReadonlyArray<AppNotification>> =
    this.toastList.asReadonly();
  readonly history: Signal<ReadonlyArray<AppNotification>> =
    this.historyList.asReadonly();
  readonly unread: Signal<number> = this.unreadCount.asReadonly();
  readonly hasUnreadError: Signal<boolean> = computed((): boolean =>
    this.historyList()
      .slice(0, this.unreadCount())
      .some(
        (n: AppNotification): boolean => n.severity === ToasterSeverities.Error
      )
  );

  push(
    severity: ToasterSeverities,
    message: string,
    detail?: string,
    extra: NotificationExtras = {}
  ): AppNotification {
    const { params, title, where } = extra;
    const now = Date.now();
    // A repeat of a toast that is still on screen (same severity, message and
    // detail) bumps its count and restarts its timer instead of stacking a
    // copy - bulk actions used to fire the same toast five times in a row.
    const repeat = this.toastList().find(
      (n: AppNotification): boolean =>
        n.severity === severity &&
        n.message === message &&
        n.detail === detail &&
        JSON.stringify(n.params) === JSON.stringify(params)
    );

    if (repeat) {
      const bumped: AppNotification = { ...repeat, count: repeat.count + 1 };
      this.toastList.update((list) =>
        list.map((n) => (n.id === repeat.id ? bumped : n))
      );
      this.historyList.update((list) =>
        list.map((n) => (n.id === repeat.id ? bumped : n))
      );
      this.restartTimer(bumped);
      return bumped;
    }

    const notification: AppNotification = {
      id: this.nextId++,
      severity,
      title,
      message,
      params,
      where,
      detail,
      at: new Date(now),
      count: 1,
    };

    this.toastList.update((list) => {
      const next = [...list, notification];
      // Make room by dropping the oldest non-error first; errors are the ones
      // someone has to act on.
      while (next.length > MAX_VISIBLE_TOASTS) {
        const i = next.findIndex((n) => n.severity !== ToasterSeverities.Error);
        const [dropped] = next.splice(i === -1 ? 0 : i, 1);
        this.clearTimer(dropped.id);
      }
      return next;
    });
    this.historyList.update((list) =>
      [notification, ...list].slice(0, HISTORY_LIMIT)
    );
    this.unreadCount.update((n) => Math.min(n + 1, HISTORY_LIMIT));
    this.restartTimer(notification);
    return notification;
  }

  dismiss(id: number): void {
    this.clearTimer(id);
    this.toastList.update((list) => list.filter((n) => n.id !== id));
  }

  dismissAll(): void {
    this.toastList().forEach((n) => this.clearTimer(n.id));
    this.toastList.set([]);
  }

  /** Hovering or focusing a toast holds it on screen. */
  pause(id: number): void {
    const timer = this.timers.get(id);
    if (!timer?.handle) {
      return;
    }
    clearTimeout(timer.handle);
    timer.handle = undefined;
    timer.remaining = Math.max(
      0,
      timer.remaining - (Date.now() - timer.startedAt)
    );
  }

  resume(id: number): void {
    const timer = this.timers.get(id);
    if (!timer || timer.handle) {
      return;
    }
    timer.startedAt = Date.now();
    timer.handle = setTimeout(() => this.dismiss(id), timer.remaining);
  }

  markAllRead(): void {
    this.unreadCount.set(0);
  }

  clearHistory(): void {
    this.historyList.set([]);
    this.unreadCount.set(0);
  }

  /** How long a toast stays up; undefined for errors, which never time out. */
  durationFor(severity: ToasterSeverities): number | undefined {
    return severity === ToasterSeverities.Error ? undefined : TOAST_DURATION_MS;
  }

  private restartTimer(notification: AppNotification): void {
    this.clearTimer(notification.id);
    const duration = this.durationFor(notification.severity);
    if (duration === undefined) {
      return;
    }
    this.timers.set(notification.id, {
      remaining: duration,
      startedAt: Date.now(),
      handle: setTimeout(() => this.dismiss(notification.id), duration),
    });
  }

  private clearTimer(id: number): void {
    const timer = this.timers.get(id);
    if (timer?.handle) {
      clearTimeout(timer.handle);
    }
    this.timers.delete(id);
  }
}
